import * as React from "react"
import { Alert, Box, Button, TextField } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop"
import { useSnackbar } from 'notistack';
import ServiceOrderQuoteInvoiceService from "./ServiceOrderQuoteInvoiceService";
import { formatCustomerName, formatPhoneNumber, moneyFormat } from "../../Utils"
import ChangeServiceOrderStatusModal from "./Modals/ChangeServiceStatusModal"
import dayjs from "dayjs"
import { useTrackedServiceInputInMinutes, useTrackedUser } from "../UserState"
import CustomerModal from "../CustomerModal"
import ServiceOrderQuoteInvoiceExtras from "./ServiceOrderQuoteInvoiceExtras"
import { findLocation, useLocations } from "../ReactQuery/QueryLocations"
import ServiceOrderPreferredContact from "./ServiceOrderPreferredContact"
import SendQuoteInvoiceModal from "./Modals/SendQuoteInvoiceModal"
import { serviceOrderChip } from "../../Utils/service"

const CheckIfFullyQuoted = (serviceOrder) => {
    let foundUnquoted = false
    if (serviceOrder.service_order_services.length === 0) {
        return false
    }
    serviceOrder.service_order_services.forEach((service) => {
        if (service.quoted_flat_rate === null) {
            foundUnquoted = true
        }
        service.service_order_parts.forEach((part) => {
            if (part.quoted_cost === null) {
                foundUnquoted = true
            }
        })
    })
    return !foundUnquoted
}

export default function ServiceOrderQuoteInvoice({
    serviceOrder,
    setServiceOrder,
    setMadeUpdate,
    calculatedCosts,
    setCalculatedCosts,
    setTab,
    isQuote = false,
    warrantiedServices,
    warrantiedParts,
    warrantiedCharges
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const [inputInMinutes,] = useTrackedServiceInputInMinutes();
    const { data: locations, } = useLocations();
    const [serviceOrderSavingStep, setServiceOrderSavingStep] = React.useState(0)
    const [loadingPreviewStep, setLoadingPreviewStep] = React.useState(0)
    // const [invoiceOptionsMenuAnchorElement, setInvoiceOptionsMenuAnchorElement] = React.useState(null);
    // const [invoiceOptionsMenuItemOpen, setInvoiceOptionsMenuItemOpen] = React.useState(false);
    const [statusModalOpen, setStatusModalOpen] = React.useState({
        open: false,
        status_types: [],
        allow_notes: true,
        callback: null
    });
    const [sendQuoteInvoiceModalOpen, setSendQuoteInvoiceModalOpen] = React.useState(false)
    const [customerModalOpen, setCustomerModalOpen] = React.useState(false)
    const incompleteWarranties = (serviceOrder.service_order_warranties ?? []).some((w) => !w.approved_for_discount)
    const incompleteServiceParts = ((serviceOrder.service_order_services ?? []).some((s) => !['COMPLETED', 'DECLINED'].includes(s.status)) ||
        (serviceOrder.service_order_services ?? []).map((x) => x.service_order_parts).flat().some((p) => !['INSTALLED', 'DECLINED'].includes(p.status_type)))


    const tax_rates = {};
    const quoted_tax_rates = {};
    let total_quoted_cost = 0;
    let total_cost = 0;
    let total_warranties_service = 0;
    let total_warranties_parts = 0;
    let total_warranties_charges = 0;

    Object.keys(warrantiedServices).forEach((service_id) => {

        Object.keys(warrantiedServices[service_id] ?? {}).forEach((warranty_id) => {
            const estimate = Math.round(parseFloat(warrantiedServices[service_id][warranty_id].association.estimated_coverage) * 100) / 100
            total_warranties_service += estimate
        })
    });

    Object.keys(warrantiedParts).forEach((part_id) => {
        const estimate = Math.round(Object.keys(warrantiedParts[part_id] ?? {}).reduce((accumulator, warranty_id) => accumulator + parseFloat(warrantiedParts[part_id][warranty_id].association.estimated_coverage), 0) * 100) / 100
        total_warranties_parts += estimate
    });

    Object.keys(warrantiedCharges).forEach((charge_id) => {
        const estimate = Math.round(Object.keys(warrantiedCharges[charge_id] ?? {}).reduce((accumulator, warranty_id) => accumulator + parseFloat(warrantiedCharges[charge_id][warranty_id].association.estimated_coverage), 0) * 100) / 100
        total_warranties_charges += estimate
    });
    const total_warranties = total_warranties_service + total_warranties_parts + total_warranties_charges;

    serviceOrder.service_order_services.forEach((service) => {
        const calc_service = calculatedCosts.services?.[service.id]
        const warranties = warrantiedServices[service.id] ?? {}
        let total_warranty = 0
        Object.keys(warranties).forEach((warranty_id) => {
            const estimate = Math.round(parseFloat(warranties[warranty_id].association.estimated_coverage) * 100) / 100
            total_warranty += estimate
        })
        let flat_rate = (Math.round(parseFloat(calc_service?.flat_rate || 0) * 100) / 100) - total_warranty;
        const hourly_rate = parseFloat(calc_service?.hourly_rate || 0);
        const minutes_worked = parseFloat(calc_service?.minutes_worked || 0) * (inputInMinutes ? 1 : 60);
        let labor_costs = Math.round((hourly_rate * minutes_worked / 60) * 100) / 100
        if (flat_rate < 0) {
            labor_costs += flat_rate // Add because flat_rate is negative
            flat_rate = 0
        }

        total_cost += flat_rate + labor_costs;

        let quoted_flat_rate = (Math.round((service.quoted_flat_rate ?? 0) * 100) / 100) - total_warranty;
        const quoted_hourly_rate = parseFloat(service.quoted_hourly_rate ?? 0);
        const quoted_minutes_worked = parseFloat(service.quoted_minutes_worked ?? 0);
        let quoted_labor_costs = Math.round((quoted_hourly_rate * quoted_minutes_worked / 60) * 100) / 100
        if (quoted_flat_rate < 0) {
            quoted_labor_costs += quoted_flat_rate
            quoted_flat_rate = 0
        }

        total_quoted_cost += quoted_flat_rate + quoted_labor_costs;

        if (service.taxable_flat_rate) {
            tax_rates[serviceOrder.location_tax_rate.toFixed(2)] = (tax_rates[serviceOrder.location_tax_rate.toFixed(2)] || 0) + flat_rate;
            quoted_tax_rates[serviceOrder.location_tax_rate.toFixed(2)] = (quoted_tax_rates[serviceOrder.location_tax_rate.toFixed(2)] || 0) + quoted_flat_rate;
        }
        if (service.taxable_hourly_rate) {
            tax_rates[serviceOrder.location_tax_rate.toFixed(2)] = (tax_rates[serviceOrder.location_tax_rate.toFixed(2)] || 0) + Math.round((hourly_rate * minutes_worked / 60) * 100) / 100;
            quoted_tax_rates[serviceOrder.location_tax_rate.toFixed(2)] = (quoted_tax_rates[serviceOrder.location_tax_rate.toFixed(2)] || 0) + Math.round((quoted_hourly_rate * quoted_minutes_worked / 60) * 100) / 100;
        }

        Object.values(service.service_order_parts).forEach((service_part) => {
            const part = calc_service?.parts?.[service_part.id];
            const warranties = warrantiedParts[service_part.id] ?? {}
            let part_total_warranty = 0
            Object.keys(warranties).forEach((warranty_id) => {
                const estimate = Math.round(parseFloat(warranties[warranty_id].association.estimated_coverage) * 100) / 100
                part_total_warranty += estimate
            })

            const part_cost = (Math.round(((parseFloat(part?.actual_cost) * parseFloat(part?.quantity)) || 0) * 100) / 100) - part_total_warranty;

            total_cost += part_cost;

            const quoted_part_cost = (Math.round(((parseFloat(part?.quoted_cost ?? 0) * parseFloat(part?.quoted_quantity ?? 0)) || 0) * 100) / 100) - part_total_warranty;

            total_quoted_cost += quoted_part_cost

            if (service_part.taxable) {
                tax_rates[serviceOrder.location_tax_rate.toFixed(2)] = (tax_rates[serviceOrder.location_tax_rate.toFixed(2)] || 0) + part_cost;
                quoted_tax_rates[serviceOrder.location_tax_rate.toFixed(2)] = (quoted_tax_rates[serviceOrder.location_tax_rate.toFixed(2)] || 0) + quoted_part_cost;
            }
        });
    });

    const shop_fees_warranties = warrantiedCharges[serviceOrder.service_order_charges.find((x) => x.charge_type === 'SHOP_FEES')?.id] ?? {}
    let shop_fees_total_warranty = 0
    Object.keys(shop_fees_warranties).forEach((warranty_id) => {
        const estimate = Math.round(parseFloat(shop_fees_warranties[warranty_id].association.estimated_coverage) * 100) / 100
        shop_fees_total_warranty += estimate
    })
    const shop_fees = (Math.round((parseFloat(calculatedCosts.shop_fees_manual ?? calculatedCosts.shop_fees_calculated ?? 0)) * 100) / 100) - shop_fees_total_warranty;
    if (shop_fees && calculatedCosts.shop_fees_tax_rate) {
        tax_rates[calculatedCosts.shop_fees_tax_rate.toFixed(2)] = (tax_rates[calculatedCosts.shop_fees_tax_rate.toFixed(2)] || 0) + shop_fees;
    }
    total_cost += shop_fees;

    const quoted_shop_fees = (serviceOrder.service_order_charges.find((x) => x.charge_type === 'SHOP_FEES')?.estimated_cost) - shop_fees_total_warranty
    if (quoted_shop_fees && calculatedCosts.shop_fees_tax_rate) {
        quoted_tax_rates[calculatedCosts.shop_fees_tax_rate.toFixed(2)] = (quoted_tax_rates[calculatedCosts.shop_fees_tax_rate.toFixed(2)] || 0) + quoted_shop_fees;
    }
    total_quoted_cost += quoted_shop_fees

    Object.values(calculatedCosts.adjustments).forEach((adjustment) => {
        const warranties = warrantiedCharges[adjustment.id] ?? {}
        let total_warranty = 0
        Object.keys(warranties).forEach((warranty_id) => {
            const estimate = Math.round(parseFloat(warranties[warranty_id].association.estimated_coverage) * 100) / 100
            total_warranty += estimate
        })
        const adjustment_cost = (Math.round(parseFloat(adjustment?.actual_cost ?? 0) * 100) / 100) - total_warranty;
        const quoted_adjustment_cost = (Math.round((serviceOrder.service_order_charges.find((x) => x.id === adjustment.id)?.estimated_cost ?? 0) * 100) / 100) - total_warranty;
        total_cost += adjustment_cost;
        total_quoted_cost += quoted_adjustment_cost;
        if (adjustment.tax_rate) {
            tax_rates[adjustment.tax_rate.toFixed(2)] = (tax_rates[adjustment.tax_rate.toFixed(2)] || 0) + adjustment_cost;
            quoted_tax_rates[adjustment.tax_rate.toFixed(2)] = (quoted_tax_rates[adjustment.tax_rate.toFixed(2)] || 0) + quoted_adjustment_cost;
        }
    })
    Object.values(calculatedCosts.fees).forEach((fee) => {
        const warranties = warrantiedCharges[fee.id] ?? {}
        let total_warranty = 0
        Object.keys(warranties).forEach((warranty_id) => {
            const estimate = Math.round(parseFloat(warranties[warranty_id].association.estimated_coverage) * 100) / 100
            total_warranty += estimate
        })
        const fee_cost = (Math.round(parseFloat((isQuote ? fee.estimated_cost : fee.actual_cost) ?? 0) * 100) / 100) - total_warranty
        total_cost += fee_cost;
        const quoted_fee_cost = (Math.round(parseFloat(serviceOrder.service_order_charges.find((x) => x.id === fee.id)?.estimated_cost ?? 0) * 100) / 100) - total_warranty
        total_quoted_cost += quoted_fee_cost
        if (fee.tax_rate) {
            tax_rates[fee.tax_rate.toFixed(2)] = (tax_rates[fee.tax_rate.toFixed(2)] || 0) + fee_cost;
            quoted_tax_rates[fee.tax_rate.toFixed(2)] = (quoted_tax_rates[fee.tax_rate.toFixed(2)] || 0) + quoted_fee_cost;
        }
    })

    const total_tax = Object.keys(tax_rates).reduce((accumulator, rate) => accumulator + (Math.round(rate * tax_rates[rate]) / 100), 0);
    const total_total_cost = total_cost + total_tax;

    const total_quoted_tax = Object.keys(quoted_tax_rates).reduce((accumulator, rate) => accumulator + (rate * quoted_tax_rates[rate] / 100), 0);
    const total_total_quoted_cost = total_quoted_cost + total_quoted_tax;
    const fully_quoted = CheckIfFullyQuoted(serviceOrder)

    // const openInvoiceOptionsMenu = (event) => {
    //     setInvoiceOptionsMenuAnchorElement(event.currentTarget.parentElement)
    //     setInvoiceOptionsMenuItemOpen(true)
    // }


    const submitCalculations = (skip_status) => {
        const callback = (status, notes, customer_communication_object) => {
            setServiceOrderSavingStep(1)
            const my_calculatedCosts = window.structuredClone(calculatedCosts)
            if (my_calculatedCosts.shop_fees_calculated !== undefined) {
                my_calculatedCosts.shop_fees = my_calculatedCosts.shop_fees_manual ?? my_calculatedCosts.shop_fees_calculated
                if (my_calculatedCosts.shop_fees === my_calculatedCosts.shop_fees_manual && my_calculatedCosts.shop_fees_calculated !== my_calculatedCosts.shop_fees_manual) {
                    my_calculatedCosts.manual_shop_fee = true
                }
                my_calculatedCosts.shop_fees = parseFloat(my_calculatedCosts.shop_fees)
            }
            if (!inputInMinutes) {
                Object.values(my_calculatedCosts.services).forEach((service) => {
                    service.minutes_worked = Math.round(service.minutes_worked * 60 * 100000) / 100000
                })
            }
            const body = {
                calculatedCosts: my_calculatedCosts,
                'skip_status': skip_status,
                'status': status,
                'notes': notes.trim()
            }
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/${isQuote ? 'submit_quote_calculations' : 'submit_payment_calculations'}`, body).then(
                    (serviceOrderNew) => {
                        enqueueSnackbar(`Successfully prepared ${isQuote ? 'quote' : 'invoice'}`, { variant: 'success' }) // TODO: Break out message and show things like updated vs prepared
                        setMadeUpdate(true)
                        if (customer_communication_object) {
                            send_authorized_request(() =>
                                AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/send_customer_communication`, customer_communication_object).then((serviceOrderNewNew) => {
                                    enqueueSnackbar(`Successfully sent ${customer_communication_object.destination_type === 'email' ? 'email' : 'text'} to customer (${customer_communication_object.destination_type === 'email' ? customer_communication_object.destination : formatPhoneNumber(customer_communication_object.destination)})`, { variant: 'success' })
                                    setServiceOrderSavingStep(0)
                                    setServiceOrder(serviceOrderNewNew.data.data)
                                }).catch((error) => {
                                    setServiceOrderSavingStep(0)
                                    enqueueSnackbar(`Failed to send ${customer_communication_object.destination_type === 'email' ? 'email' : 'text'} to customer (${customer_communication_object.destination_type === 'email' ? customer_communication_object.destination : formatPhoneNumber(customer_communication_object.destination)}) <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                                    setServiceOrder(serviceOrderNew.data.data)
                                }))
                        } else {
                            setServiceOrder(serviceOrderNew.data.data)
                            setServiceOrderSavingStep(0)
                        }
                        if (!isQuote && !skip_status) {
                            setTab('payment')
                        }
                    }
                ).catch((error) => {
                    setServiceOrderSavingStep(0)
                    enqueueSnackbar(`Failed to prepare ${isQuote ? 'quote' : 'invoice'} <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        }
        if (isQuote || serviceOrder.status_type === 'INVOICED' || skip_status) {
            callback(serviceOrder.status_name, '')
        } else {
            setStatusModalOpen({
                open: true,
                allow_same_status_selection: isQuote,
                status_types: ['INVOICED'],
                allow_notes: true,
                title: 'Are you sure you want to submit these calculations?',
                callback: (change, status, notes, customer_communication_object) => {
                    if (change) {
                        callback(status, notes, customer_communication_object)
                    }
                    setStatusModalOpen({ open: false, status_types: [], allow_notes: true, callback: null })
                }
            })
        }
    }

    const previewQuote = () => {
        setLoadingPreviewStep(1)
        send_authorized_request(() => AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/preview_quote`, {}).then((response) => {
            setLoadingPreviewStep(0)
            const url = response.data.data.replace(/.*\/\/[^/]*/, window.location.origin)
            window.open(url, '_blank').focus()
        }).catch((error) => {
            console.log(error);
            setLoadingPreviewStep(0);
            enqueueSnackbar(`Failed to preview quote: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
        }))
    }

    return (
        (<Box>
            <LoadingBackdropIndeterminate
                open={serviceOrderSavingStep > 0}
                text={"Saving Order"}
            />
            <LoadingBackdropIndeterminate
                open={loadingPreviewStep > 0}
                text={"Generating Preview"}
            />
            {!isQuote && incompleteWarranties &&
                <Alert severity="error">All warranty claims must be approved</Alert>
            }
            <Grid container columns={{ xs: 6, sm: 6, md: 12 }} rowSpacing={1}>
                <Grid container columns={{ xs: 6, sm: 12 }} alignItems={"center"} size={6}>
                    <Grid container alignItems={'center'} rowSpacing={0} size={6}>
                        <h3>Order Details</h3>
                        <Grid size={12}>
                            <span>Order: #{serviceOrder.id}</span>
                        </Grid>
                        <Grid size={12}>
                            <span>Order Status: {serviceOrderChip(my_user, serviceOrder.status_name, serviceOrder.status_type)}</span>
                        </Grid>
                        <Grid size={12}>
                            <span>Customer:</span><Button onClick={() => setCustomerModalOpen(true)}>{formatCustomerName(serviceOrder.customer)}</Button>
                        </Grid>
                        <Grid size={"auto"}>
                            <span>Preferred Contact:</span>
                        </Grid>
                        <Grid size="grow">
                            <ServiceOrderPreferredContact
                                serviceOrder={serviceOrder}
                                setServiceOrder={setServiceOrder}
                                setServiceOrderSavingStep={setServiceOrderSavingStep}
                                editable={true}
                            />
                        </Grid>
                        <Grid size={12}>
                            <span>Item: {serviceOrder.customer_item.manufacturer} {serviceOrder.customer_item.model}</span>
                        </Grid>
                    </Grid>
                    {isQuote ?
                        <Grid container size={6} spacing={1}>
                            <Grid size="auto">
                                <Box>
                                    <Button color='primary' variant='contained' disabled={!fully_quoted} onClick={() => setSendQuoteInvoiceModalOpen(true)}>
                                        {serviceOrder.customer_approved_status !== 'NOT_REQUESTED' ? 'Resend' : 'Send'} Quote
                                    </Button>
                                </Box>
                                {!fully_quoted &&
                                    <Box sx={{ color: 'error.main' }}>
                                        <span>Service or Part not quoted</span>
                                    </Box>
                                }
                            </Grid>
                            <Grid size="auto">
                                <Button onClick={previewQuote} disabled={!fully_quoted} variant="outlined" color="primary">
                                    <span>Preview Quote</span>
                                </Button>
                            </Grid>
                        </Grid>
                        :
                        <Grid size={6}></Grid>
                        // <Grid size={6}>
                        //     <ButtonGroup color='primary' variant='contained'>
                        //         <Button disabled={['WORK_COMPLETE'].includes(serviceOrder.status_type)} onClick={sendInvoice}>Send Invoice</Button>
                        //         <Button disabled={['WORK_COMPLETE'].includes(serviceOrder.status_type)} sx={{ p: '6px' }} onClick={openInvoiceOptionsMenu}><ArrowDropDownIcon /></Button>
                        //         {/* {TODO: DISABLE IF QUOTE NOT CREATED} */}
                        //     </ButtonGroup>
                        //     <Menu
                        //         id={'invoiceoptionsmenu'}
                        //         anchorEl={invoiceOptionsMenuAnchorElement}
                        //         open={invoiceOptionsMenuItemOpen}
                        //         onClose={() => setInvoiceOptionsMenuItemOpen(false)}
                        //         MenuListProps={{
                        //             'aria-labelledby': 'basic-button',
                        //         }}
                        //     >
                        //         <MenuItem disabled={!serviceOrder.customer_item.customer.email} onClick={undefined}>Send As Email ({serviceOrder.customer_item.customer.email ?? "NO EMAIL"})</MenuItem>
                        //     </Menu>
                        // </Grid>
                    }
                </Grid>
                <Grid size={6}>
                    <h4 style={{ marginBottom: 12 }}>Order Summary Notes (Customer Visible)</h4>
                    <TextField
                        margin="dense"
                        id="summarynotes"
                        type="text"
                        placeholder="Overview of Service Order"
                        fullWidth
                        variant="filled"
                        multiline
                        minRows={3}
                        value={calculatedCosts.summary_notes}
                        onChange={(event) => setCalculatedCosts({ ...calculatedCosts, summary_notes: event.target.value })}
                        sx={{ mt: 0, pt: 0 }}
                        slotProps={{
                            input: { maxLength: 2055, style: { paddingTop: 12 } },
                            inputLabel: { shrink: false }
                        }} />
                </Grid>
            </Grid>
            {
                serviceOrder.service_order_services.toSorted((a, b) => dayjs.utc(b.date_created).unix() - dayjs.utc(a.date_created).unix()).map((service) => (
                    <ServiceOrderQuoteInvoiceService
                        key={service.id}
                        serviceOrder={serviceOrder}
                        service={service}
                        calculatedCosts={calculatedCosts}
                        setCalculatedCosts={setCalculatedCosts}
                        isQuote={isQuote}
                        warrantiedServices={warrantiedServices}
                        warrantiedParts={warrantiedParts}
                    />)
                )
            }
            <ServiceOrderQuoteInvoiceExtras
                serviceOrder={serviceOrder}
                calculatedCosts={calculatedCosts}
                setCalculatedCosts={setCalculatedCosts}
                isQuote={isQuote}
                warrantiedCharges={warrantiedCharges}
            />
            {
                !isQuote &&
                <Grid container justifyContent={"flex-end"} alignItems={"center"} sx={{ textAlign: 'right', mr: 1, pr: 1 }} columns={15}>
                    <Grid size={9}>
                        <span></span>
                    </Grid>
                    <Grid size={3}>
                        <span>Quoted</span>
                    </Grid>
                    <Grid size={3}>
                        <span>Final</span>
                    </Grid>
                </Grid>
            }
            <Grid container justifyContent={"flex-end"} alignItems={"center"} sx={{ textAlign: 'right', mr: 1, pr: 1 }} columns={12 + (!isQuote && 3)}>
                <Grid size={9}>
                    <span>Total Before Tax:</span>
                </Grid>
                {!isQuote &&
                    <Grid size={3}>
                        <span>{`$${moneyFormat(total_quoted_cost + total_warranties)}`}</span>
                    </Grid>
                }
                <Grid size={3}>
                    <span>${moneyFormat(total_cost + total_warranties)}</span>
                </Grid>
            </Grid>
            {
                total_warranties > 0 &&
                <Grid container justifyContent={"flex-end"} alignItems={"center"} sx={{ textAlign: 'right', mr: 1, pr: 1 }} columns={12 + (!isQuote && 3)}>
                    <Grid size={9}>
                        <span>Total With Warranties:</span>
                    </Grid>
                    {!isQuote &&
                        <Grid size={3}>
                            <span>${moneyFormat(total_quoted_cost)}</span>
                        </Grid>
                    }
                    <Grid size={3}>
                        <span>${moneyFormat(total_cost)}</span>
                    </Grid>
                </Grid>
            }
            <Grid container justifyContent={"flex-end"} alignItems={"center"} sx={{ textAlign: 'right', mr: 1, pr: 1 }} columns={12 + (!isQuote && 3)}>
                <Grid size={9}>
                    <span>Tax:</span>
                </Grid>
                {!isQuote &&
                    <Grid size={3}>
                        <span>{`$${moneyFormat(total_quoted_tax)}`}</span>
                    </Grid>
                }
                <Grid size={3}>
                    <span>{'$' + moneyFormat(total_tax)}</span>
                </Grid>
            </Grid>
            <Grid container justifyContent={"flex-end"} alignItems={"center"} sx={{ textAlign: 'right', mr: 1, pr: 1 }} columns={12 + (!isQuote && 3)}>
                <Grid size={9}>
                    <span>Total:</span>
                </Grid>
                {!isQuote &&
                    <Grid size={3}>
                        <span>{`$${moneyFormat(total_total_quoted_cost)}`}</span>
                    </Grid>
                }
                <Grid size={3}>
                    <span>${moneyFormat(total_total_cost)}</span>
                </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" sx={{ pr: 1, pt: 1 }} columnSpacing={1}>
                {serviceOrder.status_type === 'WORK_COMPLETE' &&
                    <Grid size="auto">
                        <Button
                            variant="contained"
                            onClick={() => submitCalculations(true)}
                            disabled={
                                incompleteWarranties ||
                                isNaN(total_total_cost) ||
                                serviceOrder.service_order_services.length === 0
                            }
                        >
                            Save Invoice Without Progressing
                        </Button>
                    </Grid>
                }
                <Grid size="auto">
                    <Button
                        variant="contained"
                        onClick={() => submitCalculations(false)}
                        disabled={
                            (!isQuote && incompleteWarranties) ||
                            (isQuote ? !['CREATED', 'IN_PROGRESS'].includes(serviceOrder.status_type) : !['WORK_COMPLETE', 'INVOICED'].includes(serviceOrder.status_type)) ||
                            isNaN(total_total_cost) ||
                            serviceOrder.service_order_services.length === 0 ||
                            (!isQuote && incompleteServiceParts)
                        }
                    >
                        {(isQuote && serviceOrder.customer_approved_status === 'NOT_REQUESTED') ||
                            (!isQuote && serviceOrder.status_type === 'WORK_COMPLETE')
                            ?
                            'Save'
                            :
                            'Update'}
                        {isQuote ? ' Quote' : ' Invoice'}
                    </Button>
                </Grid>
            </Grid>
            <CustomerModal
                customer={serviceOrder.customer}
                open={customerModalOpen}
                closeModal={(changes) => {
                    setCustomerModalOpen(false)
                    if (changes) {
                        setServiceOrder(undefined)
                        setMadeUpdate(true)
                    }
                }}
            />
            <ChangeServiceOrderStatusModal
                serviceOrder={serviceOrder}
                {...statusModalOpen}
                allow_customer_communication={true}
                default_customer_communication_type={(serviceOrder.preferred_phone && 'text') || (serviceOrder.preferred_email && 'email') || serviceOrder.customer.preferred_communication_type}
                default_phone={serviceOrder.preferred_phone || serviceOrder.customer.phone}
                default_email={serviceOrder.preferred_email || serviceOrder.customer.email}
                phones={(serviceOrder.preferred_phone ? [{ value: serviceOrder.preferred_phone, name: "Preferred Contact" }] : []).concat(
                    serviceOrder.customer.phone ? [{ value: serviceOrder.customer.phone, name: formatCustomerName(serviceOrder.customer) }] : []
                ).concat(
                    (serviceOrder.customer.customer_contacts ?? []).sort((a, b) => (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name)).map((contact) => contact.phone ? { value: contact.phone, name: `${contact.first_name} ${contact.last_name}` } : undefined).filter((x) => x !== undefined)
                )}
                emails={(serviceOrder.preferred_email ? [{ value: serviceOrder.preferred_email, name: "Preferred Contact" }] : []).concat(
                    serviceOrder.customer.email ? [{ value: serviceOrder.customer.email, name: formatCustomerName(serviceOrder.customer) }] : []
                ).concat(
                    (serviceOrder.customer.customer_contacts ?? []).sort((a, b) => (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name)).map((contact) => contact.email ? { value: contact.email, name: `${contact.first_name} ${contact.last_name}` } : undefined).filter((x) => x !== undefined)
                )}
            />
            <SendQuoteInvoiceModal
                open={sendQuoteInvoiceModalOpen}
                serviceOrder={serviceOrder}
                title="Send Quote"
                defaultTextMessage={findLocation(locations, serviceOrder.location_id)?.service_configs?.quote_text ?? ''}
                defaultEmailMessage={findLocation(locations, serviceOrder.location_id)?.service_configs?.quote_email ?? ''}
                text_hint={"Link will be added to the end of the message."}
                extra_text_character_count={` https://api.profitmaxprosuite.com/${my_user.client.id}/s/aaaabbbb`.length}
                closeModal={(type, destination, message) => {
                    setSendQuoteInvoiceModalOpen(false)
                    if (type) {
                        setServiceOrderSavingStep(1)
                        send_authorized_request(() => AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/send_quote`, { destination_type: type, destination: destination, custom_message: message }).then((serviceOrder) => {
                            enqueueSnackbar(`Successfully sent quote to ${destination}`, { variant: 'success' })
                            setServiceOrder(serviceOrder.data.data)
                            setMadeUpdate(true)
                            setServiceOrderSavingStep(0)
                        }).catch((error) => {
                            console.log(error);
                            enqueueSnackbar(`Failed to send quote: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                            setServiceOrderSavingStep(0)
                        }))
                    }
                }}
            />
        </Box >)
    );
}